import { io } from "socket.io-client";
import { useSelector } from "react-redux";
import { useEffect, useRef } from 'react';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL;

// Global singleton that survives hot reloads
let globalSocket = null;
let isConnecting = false;

const useSocketHook = () => {
    const accessToken = useSelector((state) => state.auth.accessToken);
    const socketRef = useRef(null);
    
    useEffect(() => {
        // Skip if no token or already have socket reference
        if (!accessToken || socketRef.current) {
            return;
        }
        
        // Use existing global socket or create new one
        if (globalSocket && globalSocket.connected) {
            console.log('Reusing existing global socket');
            socketRef.current = globalSocket;
            return;
        }
        
        // Prevent multiple connection attempts
        if (isConnecting) {
            return;
        }
        
        //console.log('Creating new socket connection...');
        isConnecting = true;
        
        // Clean up any existing socket
        if (globalSocket) {
            globalSocket.close();
            globalSocket = null;
        }
        
        // Create new socket with better settings
        globalSocket = io(SOCKET_URL, { 
            path: "/socket.io", 
            reconnectionAttempts: 5,
            timeout: 10000,
            auth: { token: accessToken },
            transports: ['websocket', 'polling'],
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            randomizationFactor: 0.5
        });
        
        socketRef.current = globalSocket;
        
        // Setup event handlers
        globalSocket.on('connect', () => {
            //console.log('Socket connected:', globalSocket.id);
            isConnecting = false;
        });
        
        globalSocket.on('disconnect', (reason) => {
            console.log('Socket disconnected:', reason);
            isConnecting = false;
            
            if (reason === 'io server disconnect') {
                console.log('Server disconnected, may need to reconnect manually');
            }
        });
        
        globalSocket.on('connect_error', (error) => {
            console.error('Connection error:', error.message);
            isConnecting = false;
        });
        
        globalSocket.on('reconnect_attempt', (attempt) => {
            console.log('Reconnection attempt:', attempt);
        });
        
        globalSocket.on('reconnect_failed', () => {
            console.error('Reconnection failed');
            isConnecting = false;
        });
        
        // Cleanup on component unmount (but keep socket alive)
        return () => {
            socketRef.current = null;
        };
        
    }, [accessToken]); // Only depend on accessToken
    
    return socketRef.current;
}

export default useSocketHook;